import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { eyebrow, focusRing, meta, pillAction, pressable } from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { stepPosition } from '../advisoryStepPosition';
import type { AdvisoryPath } from '../advisoryStepPosition';
import type { SupervisorEngagementState } from '../../../hooks/advisory/useSupervisorTurn';

/**
 * AdvisoryForkScreen
 *
 * With the goal confirmed, this screen's entire content is a choice
 * between two paths — nothing else. It owns no state, no effect and no
 * network call; `onChoosePath` is minted by the caller.
 *
 * It renders `AdvisoryEngagementShell` with the same position as goal
 * review (`stepPosition('goal', null)`) and prints no denominator, because
 * the fork is the tail of the same decision as goal review, not a distinct
 * step, and no path has been chosen yet.
 *
 * The shell's `children` slot used to be deliberately left unsupplied
 * entirely. It now carries the classified `engagement` summary — name,
 * confidence and a paragraph-formatted description — sourced directly from
 * `useSupervisorTurn()`'s own parsed `engagement` state, never a Firestore
 * read. The guarantee that a stray Enter keypress can never reach the
 * express action survives unchanged: the summary is read-only `Typography`
 * text, never a field, a form or anything with a default submit target, so
 * no Enter keypress can reach the express action through a native form
 * default — there is still no form for that default to belong to. This is
 * still a deliberate rejection of the prototype's own arrangement, which
 * renders the fork choice under the goal on one shared screen — exactly
 * the arrangement that would let a stray Enter keypress (e.g. one left
 * over from the goal-review feedback field) fall through onto a fork
 * action. Splitting the fork onto its own screen with no INPUT at all (a
 * summary is not an input) rules that out by construction, not by
 * convention.
 *
 * Classification completes synchronously inside the confirm-turn's own SSE
 * stream, before this screen is ever reachable — there is no second async
 * "matching" wait for this screen to narrate, which is why `engagement` is
 * rendered directly rather than behind a loading state of its own. An
 * earlier `AdvisoryMatchGate` component, which used to wrap the two step
 * bodies past this screen and narrate exactly that imagined second wait,
 * is deleted outright — its `pending`/`failed` shell had nothing left to
 * gate once classification's real timing was understood, and its
 * `noMatch`/`notSubscribed` terminals were mock-only outcomes with no
 * backend counterpart. `engagement` is `null` whenever no live SSE turn
 * has fired in this browser tab for this project AND no reload-recovered
 * engagement was found either.
 *
 * `AdvisoryEngagementPage.tsx` also supplies a Firestore-sourced fallback
 * here — its `displayedEngagement` prefers the live SSE value
 * (`sseEngagement`) exactly as before, and falls back to the goal
 * document's own `engagement` sub-object, mapped from the same shape
 * `mapGoalDoc` (`advisoryService.ts`) produces, only when that sub-object's
 * OWN status is `'classified'`. A `'classifying'` or `'engagement_failed'`
 * sub-object still resolves to `null` here — a reload landing mid-
 * classification, or after it failed, must render the same honest absence
 * as no data at all, never a stale or fabricated summary. This component
 * itself is unchanged by that: it still just renders whatever `engagement`
 * it is handed, or nothing.
 */
export interface AdvisoryForkScreenProps {
  onChoosePath: (path: AdvisoryPath) => void;
  /** The confirm turn's classified engagement, or `null` before one has arrived. */
  engagement?: SupervisorEngagementState | null;
}

export function AdvisoryForkScreen({ onChoosePath, engagement }: AdvisoryForkScreenProps) {
  const { t } = useUntypedTranslation();

  // The fork is not a counted step — it shares goal review's own position
  // and prints no denominator, since no path exists yet.
  const position = stepPosition('goal', null);

  // `description` carries real `\n\n` paragraph breaks (measured 727 chars
  // in the recorded capture) — split and render as separate text nodes,
  // never joined into one wall of text and never dangerouslySetInnerHTML
  // (ASVS V7).
  const descriptionParagraphs = engagement?.description
    ? engagement.description.split('\n\n').filter(paragraph => paragraph.trim().length > 0)
    : [];

  return (
    <AdvisoryEngagementShell
      position={position?.position ?? '02'}
      total={position?.total ?? null}
      eyebrowKey="advisory:fork.eyebrow"
      titleKey="advisory:fork.title"
      hintKey="advisory:fork.hint"
      actions={
        <>
          <Button
            variant="contained"
            disableRipple
            data-testid="advisory-fork-express"
            onClick={() => onChoosePath('express')}
            sx={[pillAction, pressable, focusRing]}
          >
            {t('advisory:fork.express')}
          </Button>
          <Button
            variant="text"
            disableRipple
            data-testid="advisory-fork-customize"
            onClick={() => onChoosePath('customize')}
            sx={[
              pressable,
              focusRing,
              theme => ({
                fontSize: theme.visualPrimitives.forkAltSize,
                color: theme.colors.inkSecondary,
                borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                paddingBottom: theme.visualPrimitives.forkAltPaddingBottom,
                borderRadius: 0,
                textTransform: 'none',
                '&:hover': {
                  color: theme.colors.inkPrimary,
                  borderBottomColor: theme.colors.inkPrimary,
                  backgroundColor: 'transparent',
                },
              }),
            ]}
          >
            {t('advisory:fork.customize')}
          </Button>
        </>
      }
    >
      {engagement ? (
        <Box data-testid="advisory-fork-engagement">
          <Typography sx={[eyebrow, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}>
            {t('advisory:fork.engagement.eyebrow')}
          </Typography>
          <Typography
            component="h2"
            data-testid="advisory-fork-engagement-name"
            sx={theme => ({
              fontSize: theme.visualPrimitives.hintSize,
              fontWeight: theme.visualPrimitives.kqTextWeight,
              color: theme.colors.inkPrimary,
              margin: 0,
              marginTop: theme.visualPrimitives.wizEyebrowGap,
            })}
          >
            {engagement.name}
          </Typography>
          <Typography
            data-testid="advisory-fork-engagement-confidence"
            sx={[meta, theme => ({ marginTop: theme.visualPrimitives.wizEyebrowGap })]}
          >
            {t('advisory:fork.engagement.confidenceLabel', { confidence: engagement.confidence })}
          </Typography>
          {descriptionParagraphs.map((paragraph, index) => (
            <Typography
              key={index}
              data-testid="advisory-fork-engagement-description-paragraph"
              sx={theme => ({
                marginTop: theme.visualPrimitives.rowGap,
                fontSize: theme.visualPrimitives.hintSize,
                lineHeight: theme.visualPrimitives.hintLineHeight,
                color: theme.colors.inkSecondary,
              })}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      ) : null}
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryForkScreen;
